/**
 * Los topes de cada plan, en un solo sitio.
 *
 * Vivían por triplicado —`apps/dashboard/src/lib/planLimits.ts`,
 * `apps/editor/src/lib/planLimits.ts` y `apps/worker/src/services/usage.ts`— y
 * ya habían divergido: el panel prometía 5 colecciones y 25 elementos en el plan
 * gratis y el servidor no conocía ninguno de los dos. Un tope que la interfaz
 * enseña y el servidor no aplica no es un tope.
 *
 * ── Qué pasa cuando sobra ────────────────────────────────────────────────────
 *
 * Nada se borra nunca. Un cliente que baja de Pro a gratis con 15 páginas
 * conserva las 15: siguen publicadas, siguen editándose y siguen sirviéndose.
 * Lo único que se bloquea es CRECER —la página 16 no llega hasta que suba de
 * plan o baje a 2—, que es lo que hacen Wix, Squarespace y Webflow, y por la
 * misma razón: borrar el trabajo de alguien porque dejó de pagar es
 * irreversible, y ningún ahorro de almacenamiento lo compensa.
 *
 * De ahí sale `allowsWrite`, que es la regla en una línea: se acepta cualquier
 * escritura que no aumente lo que ya sobra.
 */
/*
 * En `offers`, el gratis conserva los tres que tenía la agenda —era el único
 * tope de catálogo que existía— y arriba se abre: quien paga por cobrar no debe
 * tropezar con un contador cada vez que añade algo que le da dinero. Sin tope en
 * Business por lo mismo que las páginas y los contactos.
 */
export const PLAN_LIMITS = {
    free: { pages: 3, collections: 5, cmsItems: 25, contacts: 100, teamMembers: 0, offers: 3, blogPosts: 10 },
    pro: { pages: 30, collections: 20, cmsItems: 500, contacts: 10_000, teamMembers: 3, offers: 30, blogPosts: 500 },
    business: { pages: null, collections: 100, cmsItems: 5_000, contacts: null, teamMembers: null, offers: null, blogPosts: null },
};
/**
 * Cuántos proyectos puede tener una CUENTA. `null` = sin tope.
 *
 * Fuera de `PLAN_LIMITS` a propósito: eso es lo que se cuenta dentro de un
 * proyecto, y esto se cuenta contra la membresía entera. Meterlo ahí lo dejaría
 * al alcance de `overagesFor`, que recorre los topes proyecto por proyecto y
 * diría «te sobran 3 proyectos» una vez por cada sitio que tienes.
 *
 * Cuenta sólo lo que se POSEE. Un proyecto en el que entras invitado lo paga su
 * dueño —es él quien gasta el asiento, igual que en `teamMembers`—, así que
 * aceptar una invitación nunca llena el cupo de nadie más.
 *
 * Por qué existe: el plan del dueño manda en TODOS sus proyectos (ver
 * `planForProject`), así que sin tope una sola membresía Pro publica sitios
 * ilimitados con dominio propio. El tope es lo que hace que el precio sea por
 * cuenta y no por sitio.
 */
export const PROJECT_LIMITS = {
    free: 2,
    pro: 5,
    business: 20,
};
/**
 * Los proyectos de un admin, tenga el plan que tenga.
 *
 * Un número y no «sin tope»: el panel no sabía de la exención —comparaba con
 * el cupo del plan— y a un admin en el gratis le cerraba la puerta al tercer
 * proyecto aunque el servidor se lo hubiera aceptado. Con una cifra, los dos
 * dicen lo mismo y el contador de uso tiene algo que enseñar.
 */
export const ADMIN_PROJECT_LIMIT = 200;
/**
 * Cuántas fotos propias se le pueden dar a la IA al crear un sitio (el chip
 * «Fotos» del asistente).
 *
 * Cada una se describirá con una llamada a la IA, así que en el plan gratis el
 * tope cuida el techo de lo que cuesta un usuario que no paga. Doce bastan para
 * una portada y dos o tres páginas; más que eso es una galería, que se sube
 * después desde el editor.
 */
export const WIZARD_PHOTO_LIMITS = {
    free: 12,
    pro: 30,
    business: 30,
};
/**
 * Las direcciones de las páginas automáticas del catálogo.
 *
 * Existen siempre —el sitio las arma con las ofertas— y se pueden diseñar en el
 * editor como una página más. Diseñarlas no ocupa una de las páginas del plan:
 * no es una página que el cliente añada, es darle forma a una que ya estaba.
 */
export const CATALOG_INDEX_SLUGS = ['/services', '/digital', '/bookings', '/products', '/memberships'];
export function isCatalogIndexSlug(slug) {
    const clean = `/${(slug ?? '').replace(/^\/+|\/+$/g, '')}`;
    return CATALOG_INDEX_SLUGS.includes(clean);
}
/** El tope de un recurso en un plan. `null` = sin tope. */
export function limitFor(plan, resource) {
    return PLAN_LIMITS[plan][resource];
}
/**
 * Si ya no se puede añadir uno más.
 *
 * `>=` y no `>`: con el tope justo alcanzado, el siguiente ya se pasa.
 */
export function reachedLimit(used, limit) {
    return limit !== null && used >= limit;
}
/**
 * Cuántos sobran respecto al plan. 0 cuando cabe o no hay tope.
 *
 * Es lo que se le enseña a quien acaba de bajar de plan: no «tu plan permite
 * 3», sino «tienes 12 de más».
 */
export function overageOf(used, limit) {
    if (limit === null)
        return 0;
    return Math.max(0, used - limit);
}
/**
 * Si una escritura que deja `next` elementos es aceptable teniendo `current`.
 *
 * La regla del grandfathering, entera: se permite todo lo que no empeore. Por
 * debajo del tope, cualquier cosa; por encima, sólo quedarse igual o bajar. Así
 * quien tiene 15 páginas con un tope de 3 puede seguir editándolas y borrarlas,
 * pero no llegar a 16.
 */
export function allowsWrite(current, next, limit) {
    if (limit === null)
        return true;
    if (next <= limit)
        return true;
    return next <= current;
}
/** Lo que se enseña en pantalla: `∞` para lo ilimitado. */
/**
 * Un tope, escrito para leerse.
 *
 * El idioma entra por parámetro porque este paquete lo comparten el panel y el
 * worker, y ninguno de los dos tiene el mismo: el panel pinta en el idioma de
 * quien mira, el worker en el del proyecto. Estaba clavado en `es-MX`, así que
 * un panel en inglés decía «10.000» —que en inglés son diez— en cada tope de
 * cada plan, de cada widget de uso y de cada aviso de que te pasaste.
 *
 * Sin idioma se queda como estaba, que es lo que hace que los sitios que aún no
 * lo pasan no cambien de un día para otro.
 */
export function formatPlanLimit(limit, locale = 'es-MX') {
    return limit === null ? '∞' : limit.toLocaleString(locale);
}
/** El nombre comercial de cada plan. */
export const PLAN_LABEL = {
    free: 'Starter',
    pro: 'Pro',
    business: 'Business',
};
/** A qué plan se sube desde éste. `null` en el más alto → «escríbenos». */
export const NEXT_PLAN = {
    free: 'pro',
    pro: 'business',
    business: null,
};
/** Cómo se nombra cada recurso en un aviso. */
export const RESOURCE_NOUN = {
    pages: { one: 'página', many: 'páginas' },
    collections: { one: 'colección', many: 'colecciones' },
    cmsItems: { one: 'elemento CMS', many: 'elementos CMS' },
    contacts: { one: 'contacto', many: 'contactos' },
    /* «Invitado» y no «miembro»: el tope cuenta a quien ENTRA invitado, no al
       dueño, y decir «tu plan permite 3 miembros» con el dueño dentro haría
       pensar que quedan dos. */
    teamMembers: { one: 'invitado', many: 'invitados' },
    /* «Oferta publicada» y no «oferta» a secas: el tope cuenta las que están a la
       venta, y decir «tu plan permite 3 ofertas» con seis borradores guardados
       haría pensar que hay que borrar algo. */
    offers: { one: 'oferta publicada', many: 'ofertas publicadas' },
    blogPosts: { one: 'entrada publicada', many: 'entradas publicadas' },
};
/** «12 páginas» / «1 colección», para armar frases. */
export function countLabel(resource, count) {
    const noun = RESOURCE_NOUN[resource];
    return `${count.toLocaleString('es-MX')} ${count === 1 ? noun.one : noun.many}`;
}
/**
 * Todo lo que sobra en un proyecto, para el aviso de «te pasas del plan».
 *
 * Devuelve sólo lo que de verdad se pasa, en el orden en que se declaran los
 * topes: un aviso que enumera también lo que va bien no se lee.
 */
export function overagesFor(plan, used) {
    const result = [];
    for (const resource of Object.keys(PLAN_LIMITS[plan])) {
        const limit = PLAN_LIMITS[plan][resource];
        const count = used[resource];
        if (limit === null || count === undefined)
            continue;
        const over = overageOf(count, limit);
        if (over > 0)
            result.push({ resource, used: count, limit, over });
    }
    return result;
}
/**
 * Cuántas copias de seguridad conserva cada plan.
 *
 * Se cuenta por NÚMERO y no por días, que es como lo hacen Webflow y Wix: «tus
 * últimas 50 publicaciones» es una promesa que se entiende sin calendario, y no
 * caduca sola en un proyecto que lleva meses sin tocarse.
 *
 * El coste no manda aquí. Una copia son unos cientos de KB comprimidos en R2, y
 * cincuenta de ellas por proyecto salen por fracciones de céntimo al mes: esto
 * es una palanca de precio, no una restricción técnica. El tope existe para que
 * un proyecto no acumule copias eternamente, no para ahorrar.
 */
export const BACKUP_LIMITS = {
    free: 5,
    pro: 50,
    business: 100,
};
/**
 * Lo que llevaría Elite, para la página de precios.
 *
 * Aparte del registro de arriba a propósito: Elite todavía NO es un plan que el
 * servidor pueda ver —`Plan` es `free | pro | business` y no hay precio de
 * Stripe detrás—, así que no puede haber una cuenta a la que aplicarle esto.
 * Vive aquí para que la tabla comparativa lo cite desde el mismo sitio que el
 * resto y no de un número suelto, y el día que Elite exista se mueve arriba.
 */
export const ELITE_BACKUP_LIMIT = 200;
/** Los proyectos que llevaría Elite, por lo mismo y con la misma reserva. */
export const ELITE_PROJECT_LIMIT = 100;
/**
 * El peso máximo de UN vídeo subido (vídeo propio o de fondo), por plan.
 *
 * Se guardan en R2 y se sirven tal cual (MP4 o WebM): la salida de datos no se
 * cobra, así que lo que cuesta es el almacenamiento, que además cuenta contra
 * el tope del plan (`storage_bytes`). El de Business se queda por debajo de los
 * 100 MB que Cloudflare deja pasar en una petición.
 */
export const VIDEO_UPLOAD_LIMITS = {
    free: 15 * 1024 * 1024,
    pro: 50 * 1024 * 1024,
    business: 95 * 1024 * 1024,
};
/** Cuántas copias caben todavía. 0 cuando el plan está al tope. */
export function backupSlotsLeft(plan, used) {
    return Math.max(0, BACKUP_LIMITS[plan] - used);
}
/**
 * La comisión de véltica sobre lo que vende cada cliente, en porcentaje.
 *
 * Baja con el plan a propósito: quien vende mucho tiene un motivo de dinero
 * —no de funciones— para pagar, y quien vende en el gratis deja de costar y
 * empieza a sostener lo que usa. Es el modelo de Gumroad, Stan o Kajabi.
 *
 * Se cobra como `application_fee` en la cuenta conectada del cliente (cargos
 * directos, ver `services/commerce.ts` en el worker): el dinero de la venta
 * nunca pasa por nosotros, sólo nuestra parte.
 */
export const PLATFORM_FEE_PERCENT = {
    free: 5,
    pro: 2,
    business: 0,
};
/**
 * El total más bajo que puede acabar pagando el comprador.
 *
 * Hace falta porque en un pago único Stripe sólo acepta la comisión como
 * importe fijo, decidido al abrir la sesión, y el comprador aún puede escribir
 * un cupón después. Una comisión mayor que el total hace fallar el cobro, así
 * que se calcula contra el peor caso: se cobra un poco menos cuando no usa el
 * cupón, pero ninguna venta se cae por nuestra parte.
 */
export function lowestChargeAfterCoupons(amount, coupons) {
    let lowest = amount;
    for (const coupon of coupons) {
        const discounted = coupon.percentOff !== null
            ? Math.round(amount * (1 - coupon.percentOff / 100))
            : amount - (coupon.amountOff ?? 0);
        lowest = Math.min(lowest, discounted);
    }
    return Math.max(0, lowest);
}
/**
 * La comisión, en la unidad mínima de la moneda. 0 cuando no hay nada que
 * cobrar: una comisión de cero no se manda a Stripe.
 */
export function platformFeeAmount(plan, amount, coupons = []) {
    const base = lowestChargeAfterCoupons(amount, coupons);
    return Math.floor(base * PLATFORM_FEE_PERCENT[plan] / 100);
}
/**
 * Qué planes pueden conectar Stripe y cobrar. Hoy, todos.
 *
 * El gratis también vende desde el 25-sep-2026: cobrar le cuesta a véltica
 * nada —Stripe Connect Standard con cargos directos: la cuenta y los
 * contracargos son del cliente— y cada venta deja su comisión
 * (`PLATFORM_FEE_PERCENT`). Lo que empuja a pagar ya no es poder cobrar sino
 * cobrar con menos comisión y con más de tres ofertas publicadas
 * (`PLAN_LIMITS.free.offers`).
 *
 * Vive aquí, y no como un `plan !== 'free'` suelto, porque lo preguntan el
 * worker —que es quien lo aplica— y una docena de pantallas del panel que
 * sólo lo anticipan. Escrito en cada sitio, el día que cambie se queda alguno
 * prometiendo otra cosa.
 */
export const CAN_CONNECT_STRIPE = {
    free: true,
    pro: true,
    business: true,
};
/** Si una cuenta en `plan` puede conectar Stripe y cobrar. */
export function canConnectStripe(plan) {
    return CAN_CONNECT_STRIPE[plan];
}
//# sourceMappingURL=limits.js.map